import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MetaConnection = ({ clientId, onConnectionChange }) => {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Check connection status
  const checkConnectionStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/meta/connection/${clientId}`);
      const data = await response.json();
      setConnectionStatus(data);
      if (onConnectionChange) {
        onConnectionChange(data.connected);
      }
    } catch (error) {
      console.error('Error checking Meta connection:', error);
      toast.error('Failed to check Meta connection status');
    } finally {
      setLoading(false);
    }
  }, [clientId, onConnectionChange]);

  useEffect(() => {
    if (clientId) {
      checkConnectionStatus();
    }
  }, [clientId, checkConnectionStatus]);

  // Handle OAuth callback from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state && state.startsWith(clientId)) {
      handleOAuthCallback(code);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [clientId]);

  const handleOAuthCallback = async (code) => {
    try {
      setConnecting(true);
      const response = await fetch(`${API_URL}/api/meta/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          code: code
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success(`Successfully connected Meta! Found ${data.connection.ad_accounts_count} ad accounts.`);
        await checkConnectionStatus();
      } else {
        throw new Error(data.detail || 'Connection failed');
      }
    } catch (error) {
      console.error('Error connecting Meta:', error);
      toast.error('Failed to connect Meta account');
    } finally {
      setConnecting(false);
    }
  };

  const startOAuthFlow = async () => {
    try {
      setConnecting(true);
      const response = await fetch(`${API_URL}/api/meta/auth-url?client_id=${clientId}`);
      const data = await response.json();
      
      if (data.auth_url) {
        // Redirect to Facebook OAuth
        window.location.href = data.auth_url;
      } else {
        throw new Error('Failed to get auth URL');
      }
    } catch (error) {
      console.error('Error starting OAuth:', error);
      toast.error('Failed to start Meta connection');
      setConnecting(false);
    }
  };

  const syncData = async () => {
    try {
      setSyncing(true);
      const response = await fetch(`${API_URL}/api/meta/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'success' || data.status === 'partial') {
        toast.success(`Synced ${data.records_synced} records from Meta!`);
        if (data.errors && data.errors.length > 0) {
          toast.warning(`Some accounts had errors: ${data.errors.length}`);
        }
      } else {
        throw new Error(data.detail || 'Sync failed');
      }
    } catch (error) {
      console.error('Error syncing data:', error);
      toast.error('Failed to sync Meta data');
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Meta account?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/meta/connection/${clientId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Meta account disconnected');
        await checkConnectionStatus();
      } else {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect Meta account');
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500/20 animate-pulse" />
          <div className="h-4 w-48 bg-slate-700 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const isConnected = connectionStatus?.connected && !connectionStatus?.is_expired;
  const connection = connectionStatus?.connection;

  return (
    <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-xl p-6 border border-blue-500/30">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Meta Ads Integration</h3>
            <p className="text-slate-400 text-sm">
              {isConnected 
                ? `Connected as ${connection?.meta_user_name}`
                : 'Connect your Facebook & Instagram ad accounts'}
            </p>
          </div>
        </div>
        
        {isConnected && (
          <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium border border-emerald-500/30">
            Connected
          </span>
        )}
        
        {connectionStatus?.is_expired && (
          <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium border border-amber-500/30">
            Reconnection Needed
          </span>
        )}
      </div>

      {isConnected ? (
        <>
          {/* Connected state */}
          <div className="space-y-4">
            {/* Ad Accounts */}
            {connection?.ad_accounts?.length > 0 && (
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-300 mb-2">Ad Accounts ({connection.ad_accounts.length})</p>
                <div className="flex flex-wrap gap-2">
                  {connection.ad_accounts.slice(0, 5).map((account, idx) => (
                    <span key={idx} className="px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs">
                      {account.name || account.account_id}
                    </span>
                  ))}
                  {connection.ad_accounts.length > 5 && (
                    <span className="px-2 py-1 rounded bg-slate-500/20 text-slate-400 text-xs">
                      +{connection.ad_accounts.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Instagram Accounts */}
            {connection?.instagram_accounts?.length > 0 && (
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-300 mb-2">Instagram Accounts ({connection.instagram_accounts.length})</p>
                <div className="flex flex-wrap gap-2">
                  {connection.instagram_accounts.map((account, idx) => (
                    <span key={idx} className="px-2 py-1 rounded bg-pink-500/20 text-pink-300 text-xs flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      @{account.username}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Last sync info */}
            {connection?.connected_at && (
              <p className="text-xs text-slate-500">
                Connected: {new Date(connection.connected_at).toLocaleDateString()}
                {connection.token_expires_at && (
                  <> • Token expires: {new Date(connection.token_expires_at).toLocaleDateString()}</>
                )}
              </p>
            )}
            
            {/* Action buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={syncData}
                disabled={syncing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="sync-meta-btn"
              >
                {syncing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Syncing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Sync Now
                  </>
                )}
              </Button>
              
              <Button
                onClick={disconnect}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="disconnect-meta-btn"
              >
                Disconnect
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Not connected state */}
          <div className="space-y-4">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-sm text-slate-300 mb-3">
                Connect your Meta Business account to automatically sync ad performance data from Facebook and Instagram.
              </p>
              <ul className="space-y-2 text-xs text-slate-400">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Import impressions, clicks, spend, and conversions
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Automatic daily sync of campaign performance
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Support for multiple ad accounts
                </li>
              </ul>
            </div>
            
            <Button
              onClick={startOAuthFlow}
              disabled={connecting}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              data-testid="connect-meta-btn"
            >
              {connecting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Connecting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Connect Meta Business Account
                </>
              )}
            </Button>
            
            <p className="text-xs text-center text-slate-500">
              You'll be redirected to Facebook to authorize access
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default MetaConnection;
